import { updatedEntities, denormalisedEntities } from '../util/data';
import { CURRENT_USER_SHOW_SUCCESS } from './user.duck';

// ================ Action types ================ //

export const ADD_MARKETPLACE_ENTITIES = 'app/marketplaceData/ADD_MARKETPLACE_ENTITIES';

// ================ Reducer ================ //

const initialState = {
  // Database of all the fetched entities.
  entities: {},
};

const merge = (state, sdkResponse, currentUserId) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    entities: updatedEntities({ ...state.entities }, apiResponse, { currentUserId }),
  };
};

// The current user might load after the entities that contain the same user (see src/index.js,
// fetchCurrentUser is not awaited). Those entities were sanitized with the strict allowlist, so
// the stored self `user` entity is upgraded with the (already sanitized) currentUser metadata and
// publicData.
const reconcileCurrentUser = (state, currentUser) => {
  const currentUserId = currentUser?.id?.uuid;
  const { metadata, publicData } = currentUser?.attributes?.profile || {};
  const storedUser = currentUserId ? state.entities.user?.[currentUserId] : null;

  if (typeof window === 'undefined' || !storedUser || !metadata) {
    return state;
  }

  return {
    ...state,
    entities: {
      ...state.entities,
      user: {
        ...state.entities.user,
        [currentUserId]: {
          ...storedUser,
          attributes: {
            ...storedUser.attributes,
            profile: {
              ...storedUser.attributes?.profile,
              metadata,
              ...(publicData ? { publicData } : {}),
            },
          },
        },
      },
    },
  };
};

export default function marketplaceDataReducer(state = initialState, action = {}) {
  const { type, payload, meta } = action;
  switch (type) {
    case ADD_MARKETPLACE_ENTITIES:
      return merge(state, payload, meta?.currentUserId);

    case CURRENT_USER_SHOW_SUCCESS:
      return reconcileCurrentUser(state, payload);

    default:
      return state;
  }
}

// ================ Selectors ================ //

/**
 * Get the denormalised listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getListingsById = (state, listingIds) => {
  const { entities } = state.marketplaceData;
  const resources = listingIds.map(id => ({
    id,
    type: 'listing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(entities, resources, throwIfNotFound);
};

/**
 * Get the denormalised transaction entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {UUID} transactionId transaction ID to select from the store
 */
export const getTransactionById = (state, transactionId) => {
  const { entities } = state.marketplaceData;

  return entities.transaction[transactionId];
};

/**
 * Get the denormalised listing entity with the given ID
 *
 * @param {Object} state the full Redux store
 * @param {UUID} listingId listing ID to select from the store
 *
 * @return {Object} denormalised listing entity
 */
export const getListingById = (state, listingId) => {
  const { entities } = state.marketplaceData;

  return entities.listing[listingId];
};

/**
 * Get the denormalised entities from the given entity references.
 *
 * @param {Object} state the full Redux store
 *
 * @param {Array<{ id, type }} entityRefs References to entities that
 * we want to query from the data. Currently we expect that all the
 * entities have the same type.
 *
 * @return {Array<Object>} denormalised entities
 */
export const getMarketplaceEntities = (state, entityRefs) => {
  const { entities } = state.marketplaceData;
  const throwIfNotFound = false;
  return denormalisedEntities(entities, entityRefs, throwIfNotFound);
};

// ================ Action creators ================ //

export const addMarketplaceEntities = sdkResponse => ({
  type: ADD_MARKETPLACE_ENTITIES,
  payload: sdkResponse,
});
